// CRM partners + contracts + activity timeline.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Empty, Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { toast } from "../../shared/stores/toasts";

type Partner = { id: number; category: string; name: string; business_no: string | null; grade: string | null; is_active: boolean };
type Contract = { id: number; partner_id: number; title: string; status: string; start_date: string; end_date: string | null; value: number | null };
type Activity = { id: number; partner_id: number; type: string; body: string; occurred_at: string; created_by: number };

const ACT_ICON: Record<string, string> = { NOTE: "📝", CALL: "📞", MEETING: "🤝", EMAIL: "📧" };
const ACT_TYPES = ["NOTE", "CALL", "MEETING", "EMAIL"];

export default function CrmPage() {
  const [params] = useSearchParams();
  const filter = params.get("filter") || "CUSTOMER";
  const [partners, setPartners] = useState<Partner[] | null>(null);
  const [expiring, setExpiring] = useState<Contract[]>([]);
  const [selected, setSelected] = useState<Partner | null>(null);

  useEffect(() => {
    api.get<Partner[]>(`/crm/partners?category=${filter}`).then((r) => setPartners(r.data));
    api.get<Contract[]>(`/crm/contracts/expiring?days=30`).then((r) => setExpiring(r.data));
    setSelected(null);
  }, [filter]);

  return (
    <>
      <PageHeader title="거래처" subtitle={`카테고리: ${filter}`} />
      {expiring.length > 0 && (
        <div style={{ margin: "0 28px 12px" }}>
          <div className="t-card" style={{ background: "color-mix(in srgb, var(--warn) 8%, transparent)", borderColor: "var(--warn)" }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>⚠️ 30일 이내 만료 계약 {expiring.length}건</div>
            {expiring.map((c) => (
              <div key={c.id} style={{ fontSize: 13 }}>· {c.title} (~{c.end_date})</div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 12, padding: "0 28px" }}>
        {/* Partner list */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="t-grid cols-1">
            {partners === null && <Loading/>}
            {partners?.length === 0 && <Empty msg="거래처가 없습니다"/>}
            <div className="t-card" style={{ padding: 0 }}>
              <div className="t-list" style={{ padding: 16 }}>
                {partners?.map((p) => (
                  <div key={p.id} className="t-row"
                    style={{ cursor: "pointer", background: selected?.id === p.id ? "var(--p-50, #eff6ff)" : undefined }}
                    onClick={() => setSelected(selected?.id === p.id ? null : p)}>
                    <span className="title" style={{ flex: 1 }}>{p.name}</span>
                    <span className="meta">{p.business_no || "—"}</span>
                    {p.grade && <Pill kind="info">{p.grade}</Pill>}
                    <Pill kind={p.is_active ? "ok" : "muted"}>{p.is_active ? "활성" : "비활성"}</Pill>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Activity timeline panel */}
        {selected && (
          <div style={{ width: 400, flexShrink: 0 }}>
            <ActivityPanel partner={selected} />
          </div>
        )}
      </div>
    </>
  );
}

function ActivityPanel({ partner }: { partner: Partner }) {
  const [activities, setActivities] = useState<Activity[] | null>(null);
  const [form, setForm] = useState({ type: "NOTE", body: "", occurred_at: new Date().toISOString().slice(0, 16) });

  const load = () =>
    api.get<Activity[]>(`/crm/partners/${partner.id}/activities`)
      .then((r) => setActivities(r.data))
      .catch(() => setActivities([]));

  useEffect(() => { load(); }, [partner.id]);

  const add = async () => {
    if (!form.body.trim()) return;
    try {
      await api.post(`/crm/partners/${partner.id}/activities`, {
        ...form,
        occurred_at: new Date(form.occurred_at).toISOString(),
      });
      setForm({ ...form, body: "" });
      load();
    } catch (e: any) {
      toast.error("등록 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const del = async (id: number) => {
    try {
      await api.delete(`/crm/partners/${partner.id}/activities/${id}`);
      load();
    } catch {}
  };

  return (
    <div className="t-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{partner.name} — 활동 타임라인</div>

      {/* Add activity form */}
      <div style={{ display: "grid", gap: 8 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {ACT_TYPES.map((t) => (
            <button key={t} className="t-btn"
              style={{ fontSize: 11, padding: "3px 8px",
                background: form.type === t ? "var(--p-500)" : "transparent",
                color: form.type === t ? "white" : undefined }}
              onClick={() => setForm({ ...form, type: t })}>
              {ACT_ICON[t]} {t}
            </button>
          ))}
        </div>
        <input type="datetime-local" className="t-input" style={{ fontSize: 12 }}
          value={form.occurred_at}
          onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} />
        <div style={{ display: "flex", gap: 6 }}>
          <textarea className="t-input" rows={2} style={{ flex: 1, resize: "none", fontSize: 13 }}
            placeholder="활동 내용 입력…"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <button className="t-btn solid" style={{ fontSize: 12, alignSelf: "flex-end" }} onClick={add}>추가</button>
        </div>
      </div>

      {/* Timeline */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0, maxHeight: 480, overflowY: "auto" }}>
        {activities === null && <Loading />}
        {activities?.length === 0 && <Empty msg="활동 기록이 없습니다" />}
        {activities?.map((a, i) => (
          <div key={a.id} style={{ display: "flex", gap: 10, paddingBottom: 12, position: "relative" }}>
            {/* vertical line */}
            {i < (activities.length - 1) && (
              <div style={{ position: "absolute", left: 16, top: 28, bottom: 0, width: 2, background: "var(--border)" }} />
            )}
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--p-100, #dbeafe)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 14 }}>
              {ACT_ICON[a.type] ?? "•"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span style={{ fontWeight: 600, fontSize: 12 }}>{a.type}</span>
                <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                  {new Date(a.occurred_at).toLocaleDateString("ko-KR", { month: "short", day: "numeric" })}
                </span>
                <button className="t-btn" style={{ fontSize: 10, padding: "0 4px", marginLeft: "auto" }} onClick={() => del(a.id)}>×</button>
              </div>
              <div style={{ fontSize: 13, color: "var(--text-1)", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{a.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
